/**
 * Nigerian state centre coordinates.
 *
 * One lookup table used by the Weather page's location picker and the
 * FarmerHome weather chip so they stay in sync. Values are the state
 * capital's approximate lat/lon — close enough for weather (the
 * capital-vs-state-centre delta is dozens of km, which Open-Meteo's
 * ~11 km grid completely absorbs).
 *
 * Includes the 36 states + FCT (Abuja). Keys match the strings used
 * elsewhere in the app (see `data/nigeriaRegions.js:states`), which
 * matters for the "farmer's profile state → coords" fallback in
 * `lib/weatherLocation.js`.
 */

export const NIGERIA_STATE_CENTERS = {
  Abia:          { lat: 5.52,  lon: 7.49 },
  Adamawa:       { lat: 9.21,  lon: 12.46 },
  'Akwa Ibom':   { lat: 5.03,  lon: 7.92 },
  Anambra:       { lat: 6.21,  lon: 7.07 },
  Bauchi:        { lat: 10.31, lon: 9.84 },
  Bayelsa:       { lat: 4.93,  lon: 6.26 },
  Benue:         { lat: 7.73,  lon: 8.52 },
  Borno:         { lat: 11.85, lon: 13.16 },
  'Cross River': { lat: 4.95,  lon: 8.33 },
  Delta:         { lat: 6.20,  lon: 6.73 },
  Ebonyi:        { lat: 6.33,  lon: 8.11 },
  Edo:           { lat: 6.34,  lon: 5.63 },
  Ekiti:         { lat: 7.62,  lon: 5.22 },
  Enugu:         { lat: 6.44,  lon: 7.50 },
  FCT:           { lat: 9.07,  lon: 7.50 },   // Federal Capital Territory (Abuja)
  // Abuja is the state label actually used in the registration picker
  // (see `data/nigeriaRegions.js` fct region → ['Abuja']); keep an
  // alias here so profile.state === 'Abuja' resolves.
  Abuja:         { lat: 9.07,  lon: 7.50 },
  Gombe:         { lat: 10.29, lon: 11.17 },
  Imo:           { lat: 5.48,  lon: 7.03 },
  Jigawa:        { lat: 11.76, lon: 9.34 },
  Kaduna:        { lat: 10.52, lon: 7.44 },
  Kano:          { lat: 12.00, lon: 8.52 },
  Katsina:       { lat: 12.99, lon: 7.60 },
  Kebbi:         { lat: 12.45, lon: 4.20 },
  Kogi:          { lat: 7.80,  lon: 6.74 },
  Kwara:         { lat: 8.50,  lon: 4.55 },
  Lagos:         { lat: 6.52,  lon: 3.38 },
  Nasarawa:      { lat: 8.49,  lon: 8.52 },
  Niger:         { lat: 9.62,  lon: 6.55 },
  Ogun:          { lat: 7.15,  lon: 3.35 },
  Ondo:          { lat: 7.25,  lon: 5.19 },
  Osun:          { lat: 7.77,  lon: 4.56 },
  Oyo:           { lat: 7.39,  lon: 3.90 },
  Plateau:       { lat: 9.90,  lon: 8.89 },
  Rivers:        { lat: 4.82,  lon: 7.03 },
  Sokoto:        { lat: 13.06, lon: 5.24 },
  Taraba:        { lat: 8.89,  lon: 11.37 },
  Yobe:          { lat: 11.75, lon: 11.96 },
  Zamfara:       { lat: 12.17, lon: 6.66 },
};

/** Returns the state centre for `name`, or null if the name is unknown. */
export function centerOfState(name) {
  if (!name || typeof name !== 'string') return null;
  const direct = NIGERIA_STATE_CENTERS[name];
  if (direct) return direct;
  // Case-insensitive fallback — profile.state may be stored in any case.
  const folded = Object.keys(NIGERIA_STATE_CENTERS).find(
    (k) => k.toLowerCase() === name.trim().toLowerCase(),
  );
  return folded ? NIGERIA_STATE_CENTERS[folded] : null;
}

/**
 * Alphabetically sorted list of state names for pickers. FCT is
 * excluded because 'Abuja' is the label the registration flow uses
 * for the Federal Capital Territory (see `nigeriaRegions.js`) — the
 * raw 'FCT' key stays in the lookup table so legacy profile strings
 * still resolve, but we don't want two rows pointing at the same
 * place in the dropdown.
 */
export const NIGERIA_STATE_NAMES = Object.keys(NIGERIA_STATE_CENTERS)
  .filter((k) => k !== 'FCT')
  .sort((a, b) => a.localeCompare(b));
